import useQueryBuildings from "ApiLayer/Building/queryBuildings";
import { Item } from "components/atoms/Dropdown";
import ChartContainer from "components/molecules/ChartContainer";
import { BarChart } from "components/molecules/Charts";
import { CHART_COLORS } from "components/molecules/Charts/BarChart";
import { ChartNoDataState } from "components/molecules/NoDataState";
import { ReactNode, useState } from "react";
import {
    CarbonFinesForecastChartGroup,
    carbonFinesForecastChartGroupOptions,
    carbonFinesForecastChartTooltip,
} from "utils/constants";
import { getDisplayInfo } from "utils/formatting";
import { getCarbonFinesForecastAggregator, titleKey } from "./CarbonFinesForecastAggregator";
import { getCarbonFinesForecastData } from "./CarbonFinesForecastChart.helpers";

function CarbonFinesForecastChart({ filtersetUid }: { filtersetUid: string }) {
    const [carbonFinesForecastChartGroup, setCarbonFinesForecastChartGroup] =
        useState<CarbonFinesForecastChartGroup>("carbonPolicy");
    const { data: buildingsResponse, isLoading } = useQueryBuildings(filtersetUid, "reported");

    const ungroupedCarbonFinesForecastData = buildingsResponse?.buildings
        ? getCarbonFinesForecastData(buildingsResponse?.buildings)
        : [];

    const carbonFinesForecastAggregator = getCarbonFinesForecastAggregator(carbonFinesForecastChartGroup);

    const carbonFinesForecastChartData = carbonFinesForecastAggregator(ungroupedCarbonFinesForecastData);

    const chartKeys = Object.keys(carbonFinesForecastChartData.barChartData[0] || {})
        .filter((k) => k !== titleKey)
        .reverse();
    const indexKey = titleKey;

    const maxValue = Math.max(
        ...carbonFinesForecastChartData.barChartData.flatMap((row) => chartKeys.map((key) => Number(row[key]) || 0))
    );

    const { unit, formatFunction } = getDisplayInfo("carbonFinesUnsaveable", maxValue);
    const label = unit;
    const barData = carbonFinesForecastChartData.barChartData;
    const keysToColours = chartKeys.map((chartKey, i) => ({
        label: chartKey,
        color: CHART_COLORS[i],
    }));

    if (barData.length === 0 && !isLoading)
        return <ChartNoDataState title="No carbon policies - Carbon policies costs " />;

    return (
        <CarbonFinesForecastChartContainer
            setCarbonFinesForecastChartGroup={setCarbonFinesForecastChartGroup}
            isLoading={isLoading}
            cumulativeCarbonFines={carbonFinesForecastChartData.cumulativeCarbonFines}
            exportableData={barData}
        >
            <BarChart
                data={barData}
                indexBy={indexKey}
                chartKeys={chartKeys}
                chartColorsMappedToKeys={keysToColours}
                yAxisInfo={{
                    formatFunction: (v) => formatFunction(v, false),
                    label,
                }}
                xAxisInfo={{
                    tickRotation: 45,
                }}
                includeLegend={true}
            />
        </CarbonFinesForecastChartContainer>
    );
}

interface CarbonFinesForecastChartContainerProps {
    children: ReactNode;
    setCarbonFinesForecastChartGroup: Function;
    isLoading: boolean;
    cumulativeCarbonFines?: number;
    exportableData: any;
}

function CarbonFinesForecastChartContainer({
    children,
    setCarbonFinesForecastChartGroup,
    isLoading,
    cumulativeCarbonFines,
    exportableData,
}: CarbonFinesForecastChartContainerProps) {
    const items: Item[] = carbonFinesForecastChartGroupOptions.map(
        ({ key, label }: CarbonFinesForecastChartGroupOption) => ({
            id: key,
            displayValue: label,
        })
    );

    const defaultItem = items.find((item) => item.id === "carbonPolicy");
    const { unit: finesUnit, formatFunction: finesFormatFunction } = getDisplayInfo(
        "cumulativeCarbonFines",
        cumulativeCarbonFines
    );

    return (
        <ChartContainer
            title="Carbon policy costs by "
            onSelectItem={(x) => setCarbonFinesForecastChartGroup(x)}
            items={items}
            loading={isLoading}
            defaultDropdownItem={defaultItem}
            tooltip={carbonFinesForecastChartTooltip}
            exportableData={exportableData}
        >
            {cumulativeCarbonFines && (
                <h2>
                    {finesFormatFunction(cumulativeCarbonFines)} {finesUnit}
                </h2>
            )}
            {children}
        </ChartContainer>
    );
}

export default CarbonFinesForecastChart;
